import React, { useCallback, useEffect, useState } from "react";
import { Button, Drawer, Empty, Space, Spin, Table, Tag, Timeline, Typography, message } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { useGlobal } from "../../contexts/GlobalContext";
import request from "../../utils/request";
import { getErrorText } from "../../utils/errorText";

const { Text, Paragraph } = Typography;

interface RunItem {
  id: number;
  runId?: string;
  action?: string;
  status: string;
  model?: string;
  totalTokens?: number;
  costCents?: number;
  error?: string;
  createdAt: string;
}

interface TraceStep {
  id: number;
  seq: number;
  step_type: string;
  name: string;
  status: string;
  input?: string;
  output?: string;
  error?: string;
  duration_ms: number;
}

interface TraceDetail {
  run: {
    run_id: string;
    status: string;
    model?: string;
    prompt_tokens: number;
    completion_tokens: number;
    cost_cents: number;
    latency_ms: number;
    error?: string;
    created_at: string;
  };
  steps: TraceStep[];
}

const statusColor = (status: string) =>
  status === "completed" ? "green" : status === "running" ? "blue" : status === "rejected" ? "orange" : "red";

interface RunTraceTimelineProps {
  agentId: number;
  /** 触发刷新(例如调试运行结束后自增) */
  refreshKey?: number;
}

/**
 * RunTraceTimeline 运行记录 + Trace 回放(PRD v4 07§4.4):
 * 列表读 /api/v3/agents/:id/runs(agent_runs 表),点击带 runId 的记录打开
 * /api/v3/agent-runs/:runId 步骤时间线抽屉。
 */
const RunTraceTimeline: React.FC<RunTraceTimelineProps> = ({ agentId, refreshKey = 0 }) => {
  const { API_BASE_URL } = useGlobal();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [runs, setRuns] = useState<RunItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [trace, setTrace] = useState<TraceDetail | null>(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const loadRuns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await request.get(`${API_BASE_URL}/api/v3/agents/${agentId}/runs`, { params: { limit: 20 } });
      setRuns(res.data?.data || []);
    } catch (e) {
      message.error(getErrorText(e, "加载运行记录失败"));
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, agentId]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns, refreshKey]);

  const openTrace = useCallback(async (record: RunItem, syncUrl = true) => {
    if (!record.runId) {
      message.info("该记录来自旧版事件流,没有步骤级 Trace");
      return;
    }
    if (syncUrl) {
      const next = new URLSearchParams(searchParams);
      next.set("runId", record.runId);
      setSearchParams(next, { replace: true });
    }
    setDrawerOpen(true);
    setTraceLoading(true);
    setTrace(null);
    try {
      const res = await request.get(`${API_BASE_URL}/api/v3/agent-runs/${record.runId}`);
      setTrace(res.data?.data || null);
    } catch (e) {
      message.error(getErrorText(e, "加载 Trace 失败"));
    } finally {
      setTraceLoading(false);
    }
  }, [API_BASE_URL, searchParams, setSearchParams]);

  useEffect(() => {
    const runId = searchParams.get("runId");
    if (!runId || runs.length === 0 || trace?.run.run_id === runId) return;
    const record = runs.find((item) => item.runId === runId);
    if (record) {
      openTrace(record, false);
    }
  }, [openTrace, runs, searchParams, trace?.run.run_id]);

  const closeTrace = () => {
    setDrawerOpen(false);
    const next = new URLSearchParams(searchParams);
    next.delete("runId");
    setSearchParams(next, { replace: true });
  };

  return (
    <>
      <Table<RunItem>
        size="small"
        rowKey={(r) => r.runId || String(r.id)}
        loading={loading}
        dataSource={runs}
        pagination={false}
        onRow={(record) => ({ onClick: () => openTrace(record), style: { cursor: record.runId ? "pointer" : "default" } })}
        columns={[
          {
            title: "时间",
            dataIndex: "createdAt",
            width: 150,
            render: (v) => dayjs(v).format("MM-DD HH:mm:ss"),
          },
          {
            title: "状态",
            dataIndex: "status",
            width: 90,
            render: (v) => <Tag color={statusColor(v)}>{v}</Tag>,
          },
          { title: "模型", dataIndex: "model", ellipsis: true },
          { title: "Tokens", dataIndex: "totalTokens", width: 90 },
          {
            title: "成本",
            dataIndex: "costCents",
            width: 90,
            render: (v) => (v ? `${(v / 100).toFixed(2)}元` : "-"),
          },
        ]}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无运行记录" /> }}
      />

      <Drawer
        title="运行 Trace"
        width={520}
        open={drawerOpen}
        onClose={closeTrace}
        extra={
          // 效果 -> 原因 -> 成本 三者互跳(11§6.6)
          <Space>
            <Button size="small" onClick={() => navigate("/token-usage")}>
              消耗记录
            </Button>
            <Button size="small" onClick={() => navigate("/analytics")}>
              站点分析
            </Button>
          </Space>
        }
      >
        <Spin spinning={traceLoading}>
          {trace ? (
            <>
              <Paragraph>
                <Tag color={statusColor(trace.run.status)}>{trace.run.status}</Tag>
                {trace.run.model && <Tag>{trace.run.model}</Tag>}
                <Text type="secondary">
                  {trace.run.prompt_tokens + trace.run.completion_tokens} tokens · {trace.run.latency_ms}ms ·{" "}
                  {dayjs(trace.run.created_at).format("MM-DD HH:mm:ss")}
                </Text>
              </Paragraph>
              {trace.run.error && (
                <Paragraph type="danger" style={{ whiteSpace: "pre-wrap" }}>
                  {trace.run.error}
                </Paragraph>
              )}
              {trace.steps.length > 0 ? (
                <Timeline
                  items={trace.steps.map((step) => ({
                    color: step.status === "failed" ? "red" : step.status === "completed" ? "green" : "blue",
                    children: (
                      <div>
                        <Text strong>
                          #{step.seq} {step.step_type}
                          {step.name ? ` · ${step.name}` : ""}
                        </Text>
                        <Text type="secondary" style={{ marginLeft: 8 }}>
                          {step.duration_ms}ms
                        </Text>
                        {step.input && (
                          <Paragraph type="secondary" ellipsis={{ rows: 2, expandable: true }} style={{ marginBottom: 4 }}>
                            入参:{step.input}
                          </Paragraph>
                        )}
                        {step.output && (
                          <Paragraph ellipsis={{ rows: 3, expandable: true }} style={{ marginBottom: 4 }}>
                            {step.output}
                          </Paragraph>
                        )}
                        {step.error && <Paragraph type="danger">{step.error}</Paragraph>}
                      </div>
                    ),
                  }))}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="本次运行没有记录步骤(步骤级采集随 TurnLoop 改造接入)" />
              )}
            </>
          ) : (
            !traceLoading && <Empty />
          )}
        </Spin>
      </Drawer>
    </>
  );
};

export default RunTraceTimeline;
